import React from "react";
import { useTranslation } from "react-i18next";
import { panelHeading } from "../../styles";

interface Props {
  totalGoals: number;
  target: number;
  completedGwCount: number;
  totalGwCount: number;
  targetState: "BUST" | "ELIGIBLE" | "INELIGIBLE" | null;
}

export default function TargetTrackerPanel({
  totalGoals, target, completedGwCount, totalGwCount, targetState,
}: Props) {
  const { t } = useTranslation();
  const pace = completedGwCount > 0 ? totalGoals / completedGwCount : 0;
  const requiredPace = totalGwCount > 0 ? target / totalGwCount : 0;
  const projectedTotal = Math.round(pace * totalGwCount);
  const isProjectedBust = projectedTotal > target;

  const progressPct = target > 0 ? Math.min((totalGoals / target) * 100, 100) : 0;

  const statusColor =
    targetState === "BUST" ? "var(--color-danger)" :
    targetState === "INELIGIBLE" ? "oklch(0.75 0.17 60)" :
    "var(--color-success)";

  const statusLabel =
    targetState === "BUST" ? t("team.targetTracker.statusBust") :
    targetState === "INELIGIBLE" ? t("team.targetTracker.statusWaiting") :
    t("team.targetTracker.statusOnTrack");

  const barColor = isProjectedBust ? "var(--color-danger)" : "var(--color-accent)";

  const panelStyle: React.CSSProperties = {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border-subtle)",
    borderRadius: 12,
    padding: "1rem 1.25rem",
    marginBottom: "1rem",
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
        <h2 style={{ ...panelHeading, margin: 0 }}>
          {t("team.targetTracker.title")}
        </h2>
        <span style={{ fontSize: "0.7rem", fontWeight: 700, color: statusColor, display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, display: "inline-block" }} />
          {statusLabel}
        </span>
      </div>

      {/* Score / target */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.3rem", marginBottom: "0.75rem" }}>
        <span style={{ fontFamily: "var(--font-display)", fontSize: "2.8rem", fontWeight: 800, lineHeight: 1, color: "var(--color-accent)" }}>
          {totalGoals}
        </span>
        <span style={{ fontSize: "1rem", color: "var(--color-text-dim)", fontWeight: 500 }}>/ {target}</span>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.6rem", color: "var(--color-text-dim)", marginBottom: "0.3rem" }}>
          <span>{t("team.targetTracker.gwLabel", { n: 1 })}</span>
          <span>{t("team.targetTracker.gwLabel", { n: totalGwCount })}</span>
        </div>
        <div style={{ height: 5, background: "var(--color-border-subtle)", borderRadius: 3, position: "relative" }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${progressPct}%`, background: barColor, borderRadius: 3, transition: "width 0.4s ease" }} />
        </div>
        {isProjectedBust && (
          <div style={{ fontSize: "0.65rem", color: "var(--color-danger)", marginTop: "0.25rem", fontWeight: 600 }}>
            {t("team.targetTracker.projectedBust")}
          </div>
        )}
      </div>

      {/* Pace rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {[
          { label: t("team.targetTracker.paceLabel"), value: t("team.targetTracker.perGw", { n: pace.toFixed(2) }) },
          { label: t("team.targetTracker.requiredPaceLabel"), value: t("team.targetTracker.perGw", { n: requiredPace.toFixed(2) }) },
          { label: t("team.targetTracker.projectedTotalLabel"), value: t("team.targetTracker.projectedTotalValue", { total: projectedTotal, n: totalGwCount }), danger: isProjectedBust },
        ].map(({ label, value, danger }) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>{label}</span>
            <span style={{ fontSize: "0.82rem", fontWeight: 600, color: danger ? "var(--color-danger)" : "var(--color-text)" }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
